/**
 * DocumentsPanel — generic attachment list for any rPI ERP source entity.
 *
 * Usage:
 *   <DocumentsPanel
 *     sourceType="DELIVERY_NOTE"
 *     sourceId={deliveryNote.id}
 *     legalBasis="ACCOUNTING_RECORD"
 *   />
 *
 * Features (Sprint 1 MVP):
 *   - List of attached documents with filename, size, uploader, timestamp
 *   - Single-file upload CTA (legal basis fixed per mount point)
 *   - Download button (direct blob, browser save-as)
 *   - Soft delete with confirmation
 *   - Error toast via `@kgc/ui` sonner
 *
 * Deferred to Sprint 2+:
 *   - Drag-and-drop bulk upload (94-9b follow-up)
 *   - Inline PDF preview Sheet (Sprint 2)
 *   - Image thumbnail for mimeType image/*
 *   - Presigned URL download (Sprint 4+ S3 migration)
 *   - Access role picker UI (currently server-default all BOLTVEZETO+)
 *
 * @see apps/kgc-web/src/api/documents.ts — API client
 * @see apps/kgc-web/src/features/documents/hooks/useDocuments.ts — react-query hooks
 */

import type { DocumentLegalBasis, DocumentListItem, DocumentSourceType } from '@/api/documents';
import { useTranslation } from '@kgc/i18n';
import { Button, toast } from '@kgc/ui';
import { Download, FileText, Loader2, Trash2, Upload } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
import {
  useDeleteDocument,
  useDocumentsList,
  useDownloadDocument,
  useUploadDocument,
} from '../hooks/useDocuments';

interface DocumentsPanelProps {
  sourceType: DocumentSourceType;
  sourceId: string;
  /**
   * Legal basis for the attached documents. Passed through to the upload
   * request and determines Szt. 169.§ retention enforcement on the server.
   * Mount-point decides this (delivery notes → ACCOUNTING_RECORD, partner
   * docs → CONTRACT, etc.) so users cannot accidentally pick the wrong one.
   */
  legalBasis: DocumentLegalBasis;
  /**
   * Hide the upload UI when the current user only has read access.
   * Parent is responsible for checking RBAC — this prop is an explicit
   * switch, not a guess based on loaded data.
   */
  readOnly?: boolean;
}

/** Human-readable file size in KB/MB/GB. */
function formatFileSize(bytesStr: string): string {
  const bytes = Number.parseInt(bytesStr, 10);
  if (!Number.isFinite(bytes)) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

/** Format ISO timestamp as locale-aware short date + time. */
function formatDateTime(iso: string, locale: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

// ═════════════════════════════════════════════════════════════════════════

export function DocumentsPanel({
  sourceType,
  sourceId,
  legalBasis,
  readOnly = false,
}: DocumentsPanelProps) {
  const { t, locale } = useTranslation();
  const { data: documents, isLoading, isError, refetch } = useDocumentsList(sourceType, sourceId);
  const uploadMutation = useUploadDocument();
  const deleteMutation = useDeleteDocument(sourceType, sourceId);
  const downloadDoc = useDownloadDocument();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);

  // ── Upload ────────────────────────────────────────────────────────────
  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileSelected = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      // Reset the input so selecting the same file twice still fires onChange.
      e.target.value = '';
      try {
        await uploadMutation.mutateAsync({
          file,
          sourceType,
          sourceId,
          legalBasis,
        });
        toast.success(t('documents.panel.uploadSuccess'));
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        toast.error(t('documents.errors.uploadFailed', { message }));
      }
    },
    [uploadMutation, sourceType, sourceId, legalBasis, t]
  );

  // ── Download ──────────────────────────────────────────────────────────
  const handleDownload = useCallback(
    async (doc: DocumentListItem) => {
      try {
        await downloadDoc(doc.id, doc.fileName);
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        toast.error(t('documents.errors.downloadFailed', { message }));
      }
    },
    [downloadDoc, t]
  );

  // ── Delete ────────────────────────────────────────────────────────────
  const confirmDelete = useCallback(
    async (id: string) => {
      try {
        await deleteMutation.mutateAsync(id);
        toast.success(t('documents.panel.deleteSuccess'));
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        toast.error(t('documents.errors.deleteFailed', { message }));
      } finally {
        // Adversarial MEDIUM fix: reset pendingDelete even on failure so the
        // modal doesn't stay stuck indefinitely if the backend hangs.
        setPendingDelete(null);
      }
    },
    [deleteMutation, t]
  );

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      {/* Header */}
      <div className="mb-3 flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <FileText className="h-4 w-4 text-muted-foreground" />
          {t('documents.panel.title')}
          {documents && documents.length > 0 && (
            <span className="text-xs font-normal text-muted-foreground">({documents.length})</span>
          )}
        </h3>
        {!readOnly && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={handleFileSelected}
              disabled={uploadMutation.isPending}
              aria-label={t('documents.panel.uploadButton')}
            />
            <Button
              variant="outline"
              size="sm"
              onClick={handleUploadClick}
              disabled={uploadMutation.isPending}
            >
              {uploadMutation.isPending ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Upload className="mr-1.5 h-3.5 w-3.5" />
              )}
              {t('documents.panel.uploadButton')}
            </Button>
          </>
        )}
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          {t('documents.panel.loading')}
        </div>
      )}

      {/* Error */}
      {isError && (
        <div className="flex flex-col items-center gap-2 py-6 text-sm">
          <p className="text-red-500 dark:text-red-400">{t('documents.errors.loadFailed')}</p>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            {t('documents.panel.retry')}
          </Button>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !isError && (!documents || documents.length === 0) && (
        <div className="py-6 text-center text-sm text-muted-foreground">
          {t('documents.panel.empty')}
        </div>
      )}

      {/* List */}
      {!isLoading && !isError && documents && documents.length > 0 && (
        <ul className="divide-y divide-border">
          {documents.map(doc => (
            <li key={doc.id} className="flex items-center justify-between py-2.5">
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-foreground">{doc.fileName}</div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  {formatFileSize(doc.fileSize)}
                  {' · '}
                  {doc.uploadedBy}
                  {' · '}
                  {formatDateTime(doc.uploadedAt, locale)}
                </div>
              </div>
              <div className="ml-3 flex shrink-0 gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDownload(doc)}
                  aria-label={t('documents.panel.downloadButton')}
                  title={t('documents.panel.downloadButton')}
                >
                  <Download className="h-3.5 w-3.5" />
                </Button>
                {!readOnly && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setPendingDelete(doc.id)}
                    disabled={deleteMutation.isPending}
                    aria-label={t('documents.panel.deleteButton')}
                    title={t('documents.panel.deleteButton')}
                    className="text-red-500 hover:bg-red-50 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-950/30 dark:hover:text-red-300"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Inline delete confirmation */}
      {pendingDelete !== null && (
        <div className="mt-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm dark:border-red-800 dark:bg-red-950/20">
          <p className="mb-2 font-medium text-red-700 dark:text-red-300">
            {t('documents.panel.deleteConfirm')}
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setPendingDelete(null)}>
              {t('documents.panel.deleteCancel')}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => confirmDelete(pendingDelete)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
              {t('documents.panel.deleteConfirmButton')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
